import { VelocityEnv } from '@velocity-exchange/sdk';
import { RpcEndpoint } from '@velocity-exchange/common';
import { CUSTOM_RPC_LABEL } from '../../constants/constants';
import { UserSettings } from '../../environmentVariables/EnvironmentVariableTypes';
import { chooseDefaultRpc, getRpcOptions } from '../rpc';

/**
 * This file contains the logic for RPC Utils, which are not dependent on any current app-state
 */

const DEFAULT_RPC_LABEL = 'DEFAULT_RPC_LABEL';

interface RpcParamsWithValue extends Partial<RpcEndpoint> {
	value: string;
}

const getFilledRpcParams = (params: RpcParamsWithValue): RpcEndpoint => {
	return {
		label: DEFAULT_RPC_LABEL,
		allowAdditionalConnection: false,
		...params,
	};
};

/**
 * Utility / Unit-testable getRpcToUse method
 * @param usingDevnetOverride - When true, forces use of devnet RPCs
 * @param sdkEnv - The current SDK environment
 * @param currentRpcSetting - The user's saved RPC setting
 * @returns
 */
export const _GetRpcToUse = (
	usingDevnetOverride: boolean,
	sdkEnv: VelocityEnv,
	currentRpcSetting?: UserSettings['rpcEndpoint']
): RpcEndpoint => {
	const usingCustomRpc = currentRpcSetting?.label === CUSTOM_RPC_LABEL;

	if (usingCustomRpc) {
		return getFilledRpcParams(currentRpcSetting);
	}

	const rpcOptionsToUse = usingDevnetOverride
		? getRpcOptions('devnet')
		: getRpcOptions(sdkEnv);

	const selectedRpcFromOptions = rpcOptionsToUse.find(
		(rpc) => rpc.value === currentRpcSetting?.value
	);

	// if rpc saved in settings is excluded, choose a random different one
	if (!selectedRpcFromOptions) {
		return chooseDefaultRpc(rpcOptionsToUse);
	}

	if (usingDevnetOverride) {
		// When override is true, only use the selected RPC if it's for devnet
		if (selectedRpcFromOptions.value.includes('devnet')) {
			return selectedRpcFromOptions;
		}
		// If selected RPC is not devnet, ignore it and choose random devnet RPC
		return chooseDefaultRpc(rpcOptionsToUse);
	}

	// When not using override, use the selected RPC if it matches the environment
	// For mainnet environment, only use mainnet RPCs
	if (sdkEnv === 'mainnet-beta') {
		if (!selectedRpcFromOptions.value.includes('devnet')) {
			return selectedRpcFromOptions;
		}
		// If mainnet env but saved RPC is devnet, choose random mainnet
		return chooseDefaultRpc(rpcOptionsToUse);
	}

	return selectedRpcFromOptions;
};
